/**
 * Inventory Queue Bridge
 *
 * Purpose: Bridge between old localStorage queue and new IndexedDB queue
 * Maintains backward compatibility during migration period
 *
 * Status: ACTIVE - Provides legacy migration support for users upgrading from older versions
 */

import { db, type OfflineQueueItem } from '@/lib/db';
import { syncService } from '@/services/syncService';
import { logger } from '@/services/logger';
import type { SupplierProduct, ScanMode } from '@/components/types';
import { safeParseArray } from '../../utils/safeParse';

// ============================================================================
// Legacy Queue Item Interface (from old inventoryService)
// ============================================================================

interface LegacyQueueItem {
  id: string;
  type: 'updateItemQuantity' | 'logAction';
  timestamp: number;
  data: {
    // For updateItemQuantity
    ean?: string;
    delta?: number;
    productDetails?: SupplierProduct;
    reason?: ScanMode;
    // For logAction
    actionType?: string;
    details?: string;
  };
}

// ============================================================================
// Migration Helpers
// ============================================================================

/**
 * Migrate from localStorage queue to IndexedDB queue
 *
 * This is a one-time migration for users upgrading.
 */
export async function migrateLegacyQueue(): Promise<number> {
  try {
    const QUEUE_KEY = 'inventory_sync_queue';
    const stored = localStorage.getItem(QUEUE_KEY);

    if (!stored) {
      logger.debug('No legacy queue found', undefined, 'QueueBridge');
      return 0;
    }

    const legacyQueue: LegacyQueueItem[] = safeParseArray(stored, []);

    if (!Array.isArray(legacyQueue) || legacyQueue.length === 0) {
      logger.debug('Legacy queue is empty', undefined, 'QueueBridge');
      return 0;
    }

    logger.info(
      `Migrating ${legacyQueue.length} items from legacy queue`,
      undefined,
      'QueueBridge'
    );

    let migratedCount = 0;

    for (const legacyItem of legacyQueue) {
      try {
        if (legacyItem.type === 'updateItemQuantity') {
          // Convert to new format
          const newItem: Omit<OfflineQueueItem, 'id'> = {
            timestamp: legacyItem.timestamp,
            type: 'DELTA', // Default to DELTA for safety
            operation: 'adjust_inventory_delta',
            payload: {
              p_ean: legacyItem.data.ean,
              p_delta: legacyItem.data.delta,
              p_transaction_type: legacyItem.data.reason || 'COUNT',
              // Note: company_id will be added by the service layer
            },
            retries: 0,
            lastError: null,
            status: 'pending' as const,
          };

          await db.offlineQueue.add(newItem);
          migratedCount++;
        }
        // Skip logAction items (they're now handled differently)
      } catch (error) {
        logger.error('Failed to migrate queue item', error, 'QueueBridge');
      }
    }

    // Clear legacy queue
    localStorage.removeItem(QUEUE_KEY);

    logger.info(`Migrated ${migratedCount} items to new queue`, undefined, 'QueueBridge');

    return migratedCount;
  } catch (error) {
    logger.error('Failed to migrate legacy queue', error, 'QueueBridge');
    return 0;
  }
}

/**
 * Add to queue (new way)
 *
 * This wraps syncService.addToQueue with inventory-specific logic.
 */
export async function addInventoryOperationToQueue(
  ean: string,
  delta: number,
  companyId: string,
  reason: ScanMode = 'COUNT',
  productDetails?: SupplierProduct
): Promise<void> {
  // Determine operation type
  const type: 'COUNT' | 'DELTA' = reason === 'COUNT' ? 'COUNT' : 'DELTA';

  // Build payload
  const payload =
    type === 'COUNT'
      ? {
          p_company_id: companyId,
          p_ean: ean,
          p_new_quantity: delta, // For COUNT, delta IS the new quantity
          p_expected_version: 1, // TODO: Get from cached item
        }
      : {
          p_company_id: companyId,
          p_ean: ean,
          p_delta: delta,
          p_transaction_type: reason,
        };

  // Determine operation name
  const operation =
    type === 'COUNT' ? 'set_inventory_count_with_version' : 'adjust_inventory_delta';

  // Add to queue
  await syncService.addToQueue(operation, payload, type);

  logger.info(
    `Added inventory operation to queue: ${operation}`,
    { ean, delta, type },
    'QueueBridge'
  );
}

/**
 * Get legacy queue size (for backward compatibility)
 */
export function getLegacyQueueSize(): number {
  try {
    const QUEUE_KEY = 'inventory_sync_queue';
    const stored = localStorage.getItem(QUEUE_KEY);
    if (!stored) return 0;

    const queue: LegacyQueueItem[] = safeParseArray(stored, []);
    return queue.length;
  } catch (error) {
    logger.error('Failed to get legacy queue size', error, 'QueueBridge');
    return 0;
  }
}

/**
 * Get combined queue size (legacy + new)
 */
export async function getCombinedQueueSize(): Promise<number> {
  const legacySize = getLegacyQueueSize();
  const newSize = await syncService.getQueueSize();
  return legacySize + newSize;
}
